import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  IconButton,
  Tooltip,
} from '@civilgrid/ui/components';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { editAgencyLink as apiEditLink } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { AgencyLink } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import store from '../../../store';
import { buildSubRegionItems, subRegionFkValue, subRegionFormValue } from '../../../subRegionOptions';
import { items, type TFormValues } from './common';

interface Props {
  link: AgencyLink;
}

export default function EditLink({ link }: Props) {
  const { agency, subGeometries } = useSnapshot(store) as typeof store;

  const form = useForm<TFormValues>({
    defaultValues: {
      title: link.title,
      description: link.description,
      url: link.url,
      type: link.type,
      isPrivate: link.isPrivate,
      agencySubGeometryId: subRegionFormValue(link.agencySubGeometryId ?? null),
    },
  });

  const subRegionItems = useMemo(
    () =>
      buildSubRegionItems(subGeometries).map(({ value, label }) => ({
        value,
        label: <div className="text-xs">{label}</div>,
      })),
    [subGeometries],
  );

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    try {
      const res = await apiEditLink(agency!.id, link.id, {
        ...data,
        agencySubGeometryId: subRegionFkValue(data.agencySubGeometryId),
      });
      if (!res.id) throw new Error('Bad Response!');

      Notify.info({ message: 'Agency link updated' });

      if (form.formState.dirtyFields.agencySubGeometryId) {
        const fk = subRegionFkValue(data.agencySubGeometryId);
        capturePosthogEvent(
          fk
            ? POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned
            : POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberUnassigned,
          {
            agency_id: agency!.id,
            // On unassign `fk` is null; report the sub-region being vacated so
            // the event identifies which sub-region lost the member.
            sub_geometry_id: fk ?? link.agencySubGeometryId ?? null,
            member_type: 'link',
            member_id: link.id,
            entry_point: 'link_editor',
          },
        );
      }

      store.fetchData(agency!.id);
    } catch (e) {
      console.error('Error editing agency link: ', e);
      Notify.error({ message: 'Error editing agency link' });
    }
  };

  return (
    <Dialog>
      <Tooltip text="Edit link" asChild>
        <DialogTrigger asChild>
          <IconButton
            variant="hollow"
            icon="gear"
            aria-label={`Edit link ${link.title || link.url}`}
            data-testid={`agency-link-edit-${link.id}`}
          />
        </DialogTrigger>
      </Tooltip>
      <DialogContent
        title="Edit Agency Link"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`agency-link-edit-dialog-${link.id}`}
      >
        <Form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="text-sm">
            <div className="min-w-[40vw] grow space-y-2">
              <ControlledInput control={form.control} name="title" label="Title" tooltipText="Link title" isRequired />

              <ControlledTextArea
                control={form.control}
                name="description"
                label="Description"
                tooltipText="Link description"
                isRequired
              />

              <ControlledInput control={form.control} name="url" label="URL" tooltipText="Link URL" isRequired />

              <ControlledCheckbox
                control={form.control}
                name="isPrivate"
                label="Private - File is not visible to the user"
              />

              <ControlledSelect
                control={form.control}
                name="type"
                label="Type"
                tooltipText="Link type"
                isRequired
                items={items}
              />

              <ControlledSelect
                control={form.control}
                name="agencySubGeometryId"
                label="Sub Region"
                tooltipText="Assign this link to one of the agency's sub regions, or leave unscoped"
                isRequired
                items={subRegionItems}
                data-testid="agency-sub-geometries-link-sub-region-select"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="submit" disabled={!form.formState.isDirty} data-testid={`agency-link-edit-save-${link.id}`}>
              Save Link
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
